import {
  Box,
  VStack,
  Text,
  useBreakpointValue,
  Spinner,
  Badge
} from "@chakra-ui/react";
import { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { Event } from "../../services/hooks/useClassesList";
import { WeekNavigationData } from "../../types/Calendar";
import {
  groupEventsByWeek,
  filterWeekEvents,
  getWeekNavigationData,
  getWeekBounds
} from "../../utils/WeekUtils";
import { WeekNavigator } from "./WeekNavigator";
import { DayColumn } from "./DayColumn";
import { TimeColumn } from "./TimeColumn";

interface WeekCalendarViewProps {
  events: Event[];
  filteredEvents: Event[];
  isLoading?: boolean;
}

export function WeekCalendarView({
  events,
  filteredEvents,
  isLoading = false
}: WeekCalendarViewProps) {
  const [currentWeekIndex, setCurrentWeekIndex] = useState(0);
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const weeksData = useMemo(() => {
    return groupEventsByWeek(events);
  }, [events]);

  const currentWeek = useMemo(() => {
    if (!weeksData.length) return null;
    return filterWeekEvents(weeksData[currentWeekIndex], filteredEvents);
  }, [weeksData, currentWeekIndex, filteredEvents]);

  const navigationData: WeekNavigationData = useMemo(() => {
    if (!currentWeek) {
      return {
        currentWeekIndex: 0,
        totalWeeks: 0,
        hasNext: false,
        hasPrevious: false,
        currentWeekFormatted: ""
      };
    }

    return getWeekNavigationData(
      currentWeekIndex,
      weeksData.length,
      currentWeek.weekStart
    );
  }, [currentWeekIndex, weeksData.length, currentWeek]);

  const handlePreviousWeek = useCallback(() => {
    setCurrentWeekIndex(prev => Math.max(0, prev - 1));
  }, []);

  const handleNextWeek = useCallback(() => {
    setCurrentWeekIndex(prev => Math.min(weeksData.length - 1, prev + 1));
  }, [weeksData.length]);

  const handleCurrentWeek = useCallback(() => {
    const now = new Date();
    const currentWeekBounds = getWeekBounds(now);

    const currentWeekIdx = weeksData.findIndex(week =>
      week.weekStart.getTime() === currentWeekBounds.start.getTime()
    );

    setCurrentWeekIndex(currentWeekIdx >= 0 ? currentWeekIdx : 0);
  }, [weeksData]);

  useEffect(() => {
    if (weeksData.length > 0 && currentWeekIndex >= weeksData.length) {
      setCurrentWeekIndex(0);
    }
  }, [weeksData.length, currentWeekIndex]);

  useEffect(() => {
    if (scrollContainerRef.current && !isMobile) {
      scrollContainerRef.current.scrollTop = 480;
    }
  }, [isMobile]);

  if (isLoading) {
    return (
      <Box textAlign="center" py="8">
        <Spinner color="blue.900" size="xl" />
      </Box>
    );
  }

  if (!weeksData.length) {
    return (
      <Box
        bg="white"
        p="8"
        borderRadius="md"
        textAlign="center"
      >
        <Text fontSize="lg" color="blue.900" mb="2">
          Nenhuma aula encontrada
        </Text>
        <Text fontSize="md" color="gray.600">
          Não há aulas disponíveis ou que atendam aos filtros selecionados.
        </Text>
      </Box>
    );
  }

  if (!currentWeek) {
    return (
      <Box
        bg="white"
        p="8"
        borderRadius="md"
        textAlign="center"
      >
        <Text fontSize="lg" color="blue.900">
          Semana não encontrada
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={4}>
        <WeekNavigator
          navigationData={navigationData}
          onPreviousWeek={handlePreviousWeek}
          onNextWeek={handleNextWeek}
          onCurrentWeek={handleCurrentWeek}
        />
      </Box>

      {isMobile ? (
        <VStack spacing={4}>
          {currentWeek.days.map(day => (
            <DayColumn
              key={day.date.toISOString()}
              day={day}
              isMobile={true}
            />
          ))}
        </VStack>
      ) : (
        <Box
          w="100%"
          h="67vh"
          maxW="100%"
          bg="white"
          borderRadius="md"
          border="1px"
          borderColor="gray.200"
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          <Box
            display="flex"
            flexShrink={0}
            bg="white"
            borderBottom="1px"
            borderColor="gray.200"
          >
            <Box w="60px" flexShrink={0} borderRight="1px" borderColor="gray.200" />

            {currentWeek.days.map(day => (
              <Box
                key={day.date.toISOString()}
                w="calc((100% - 60px) / 7)"
                flexShrink={0}
                textAlign="center"
                p={2}
                bg="gray.50"
                borderRight="1px"
                borderColor="gray.200"
                _last={{ borderRight: "none" }}
              >
                <Text fontSize="small" color={day.isToday ? "blue.500" : "blue.900"}>
                  <strong>{day.dayName}</strong>
                </Text>
                <Text fontSize="sm" color={day.isToday ? "blue.500" : "gray.700"}>
                  <strong>{day.formattedDate}</strong>
                </Text>
                {day.events.length > 0 && (
                  <Badge colorScheme="green" fontSize="xs" mt={1}>
                    {day.events.length} aula{day.events.length > 1 ? 's' : ''}
                  </Badge>
                )}
              </Box>
            ))}
          </Box>

          <Box
            ref={scrollContainerRef}
            flex="1"
            overflowX="auto"
            overflowY="auto"
            css={{
              '&::-webkit-scrollbar': {
                height: '8px',
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'var(--chakra-colors-gray-100)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'var(--chakra-colors-gray-400)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'var(--chakra-colors-gray-500)',
              },
            }}
          >
          <Box
            display="flex"
            minH="1530px"
            w="100%"
          >
              <TimeColumn startHour={6} endHour={23} />              {currentWeek.days.map(day => (
                <Box
                  key={day.date.toISOString()}
                  w="calc((100% - 60px) / 7)"
                  minH="1530px"
                  flexShrink={0}
                  bg="gray.50"
                  borderRight="1px"
                  borderColor="gray.200"
                  _last={{ borderRight: "none" }}
                >
                  <DayColumn
                    day={day}
                    isMobile={false}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
